import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { ArrowLeft, ArrowRight, Award, CheckCircle2, XCircle, Star } from "lucide-react";
import { toast } from "sonner";

export default function Quiz() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewMode, setReviewMode] = useState("wrong");
  const [confidence, setConfidence] = useState(null);          // 1-5 selection before submit
  const [savedConfidence, setSavedConfidence] = useState(null); // set after backend acknowledges
  const [savingConfidence, setSavingConfidence] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/courses/${slug}/quiz`);
      setQuiz(data);
      setAnswers(new Array(data.questions.length).fill(null));
    })();
  }, [slug]);

  if (!quiz) return <Layout><div className="text-brand-muted">Loading assessment…</div></Layout>;

  const pick = (idx) => {
    const next = [...answers];
    next[current] = idx;
    setAnswers(next);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${slug}/quiz/submit`, { answers });
      setResult(data);
      if (data.passed) {
        try {
          const { data: p } = await api.get(`/progress/${slug}`);
          if (p?.confidence) setSavedConfidence(p.confidence);
        } catch {
          // best-effort — user can still submit confidence below
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const submitConfidence = async () => {
    if (!confidence) return;
    setSavingConfidence(true);
    try {
      await api.post(`/progress/${slug}/confidence`, { confidence });
      setSavedConfidence(confidence);
      toast.success("Confidence recorded — thank you");
    } catch (e) {
      toast.error("Could not save your rating — please try again");
    } finally {
      setSavingConfidence(false);
    }
  };

  if (result) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className={`rounded-3xl p-10 border ${result.passed ? "bg-white border-brand-linen" : "bg-white border-brand-accent/40"} shadow-card text-center`}>
            <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">Your result</div>
            <div data-testid={T.quizResultScore} className="font-serif text-8xl text-brand-ink mt-4">{result.score}%</div>
            <div className={`mt-2 text-lg ${result.passed ? "text-brand-ink" : "text-brand-accent"}`}>
              {result.correct} of {result.total} correct — {result.passed ? "you passed" : "not quite — have another go"}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              {result.passed ? (
                <Link to={`/courses/${slug}/certificate`}
                  className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover">
                  <Award className="w-4 h-4" strokeWidth={1.5} /> View course certificate
                </Link>
              ) : (
                <button data-testid={T.quizRetry} onClick={() => { setResult(null); setCurrent(0); setAnswers(new Array(quiz.questions.length).fill(null)); }}
                  className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover">
                  Retry the quiz
                </button>
              )}
              <Link to={`/courses/${slug}`} className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-6 py-3 hover:bg-brand-linen">
                Back to course
              </Link>
            </div>
          </motion.div>

          {result.passed && (
            <motion.div
              data-testid="confidence-card"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="mt-6 bg-white border border-brand-linen rounded-3xl p-6 shadow-card"
            >
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-brand-ink flex-shrink-0 mt-0.5" strokeWidth={1.7} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">Confidence check</div>
                  <h3 className="font-serif text-2xl text-brand-ink mt-1">
                    How confident do you feel applying this at the desk?
                  </h3>
                  <p className="text-sm text-brand-muted mt-1">
                    Your rating is shared only with your Practice Manager for training records. 1 = still shaky · 5 = fully confident.
                  </p>

                  {savedConfidence ? (
                    <div
                      data-testid="confidence-saved"
                      className="mt-4 inline-flex items-center gap-2 bg-brand-bone border border-brand-linen rounded-full px-4 py-2 text-sm text-brand-ink"
                    >
                      <CheckCircle2 className="w-4 h-4" strokeWidth={1.7} />
                      Recorded — you rated your confidence <strong className="mx-1">{savedConfidence}/5</strong>
                    </div>
                  ) : (
                    <>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            data-testid={`confidence-${n}`}
                            onClick={() => setConfidence(n)}
                            className={`w-12 h-12 rounded-2xl border transition-all font-serif text-xl ${
                              confidence === n
                                ? "bg-brand-ink text-brand-bone border-brand-ink"
                                : "bg-white border-brand-linen text-brand-ink hover:border-brand-ink"
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                      <div className="mt-4">
                        <button
                          data-testid="confidence-submit"
                          onClick={submitConfidence}
                          disabled={!confidence || savingConfidence}
                          className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover disabled:opacity-60"
                        >
                          {savingConfidence ? "Saving…" : "Save my rating"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          <div className="mt-10 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-2xl text-brand-ink">Learn from your answers</h3>
              <div className="inline-flex bg-brand-linen/50 border border-brand-linen rounded-full p-1 text-xs">
                <button onClick={() => setReviewMode("wrong")}
                  className={`px-3 py-1.5 rounded-full transition-colors ${reviewMode === "wrong" ? "bg-brand-ink text-brand-bone" : "text-brand-secondary hover:text-brand-ink"}`}>
                  Just what I got wrong ({result.breakdown.filter((b) => !b.is_correct).length})
                </button>
                <button onClick={() => setReviewMode("all")}
                  className={`px-3 py-1.5 rounded-full transition-colors ${reviewMode === "all" ? "bg-brand-ink text-brand-bone" : "text-brand-secondary hover:text-brand-ink"}`}>
                  All {result.breakdown.length} questions
                </button>
              </div>
            </div>

            {reviewMode === "wrong" && result.breakdown.every((b) => b.is_correct) && (
              <div className="bg-white border border-brand-linen rounded-2xl p-6 text-center">
                <CheckCircle2 className="w-6 h-6 text-brand-ink mx-auto" strokeWidth={2} />
                <div className="font-serif text-xl text-brand-ink mt-3">A clean sweep — you didn't get any wrong.</div>
                <div className="text-sm text-brand-muted mt-1">Switch to "All questions" if you'd like to review the reasoning behind each one.</div>
              </div>
            )}

            {result.breakdown
              .map((b, i) => ({ b, i }))
              .filter(({ b }) => reviewMode === "all" || !b.is_correct)
              .map(({ b, i }) => (
                <div key={i} className={`bg-white border rounded-2xl p-5 ${b.is_correct ? "border-brand-linen" : "border-brand-accent/40"}`}>
                  <div className="flex items-start gap-3">
                    {b.is_correct
                      ? <CheckCircle2 className="w-5 h-5 text-brand-ink mt-0.5 flex-shrink-0" strokeWidth={2} />
                      : <XCircle className="w-5 h-5 text-brand-accent mt-0.5 flex-shrink-0" strokeWidth={2} />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-widest text-brand-muted">
                        Question {i + 1} of {result.breakdown.length} · {b.is_correct ? "Correct" : "Incorrect"}
                      </div>
                      <div className="text-brand-ink font-medium mt-1">{b.question}</div>

                      <div className="mt-4 space-y-2">
                        {b.options && b.options.map((opt, oi) => {
                          const isRight = oi === b.correct_index;
                          const isYours = oi === b.given_index;
                          let cls = "border-brand-linen bg-brand-bone text-brand-secondary";
                          if (isRight) cls = "border-brand-ink bg-brand-ink/5 text-brand-ink font-medium";
                          if (isYours && !isRight) cls = "border-brand-accent bg-brand-accent/10 text-brand-accent";
                          return (
                            <div key={oi} className={`flex items-start gap-2 px-3 py-2 rounded-xl border text-sm ${cls}`}>
                              <div className="w-5 h-5 rounded-full bg-white border border-brand-linen flex items-center justify-center text-[11px] flex-shrink-0 mt-0.5">
                                {String.fromCharCode(65 + oi)}
                              </div>
                              <div className="flex-1">{opt}</div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {isYours && (
                                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${isRight ? "bg-brand-ink text-brand-bone" : "bg-brand-accent text-white"}`}>
                                    Your answer
                                  </span>
                                )}
                                {isRight && !isYours && (
                                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-brand-ink text-brand-bone">
                                    Correct
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {b.explanation && (
                        <div className="mt-4 pt-4 border-t border-brand-linen text-sm text-brand-body">
                          <span className="text-[11px] uppercase tracking-widest text-brand-muted mr-2">Why</span>
                          <em>{b.explanation}</em>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Layout>
    );
  }

  const q = quiz.questions[current];
  const isLast = current === quiz.questions.length - 1;
  const canProceed = answers[current] !== null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <Link to={`/courses/${slug}`} className="inline-flex items-center gap-2 text-sm text-brand-secondary hover:text-brand-ink">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to course
        </Link>

        <div className="mt-6 text-xs uppercase tracking-[0.28em] text-brand-muted">
          {quiz.title} · Question {current + 1} of {quiz.questions.length}
        </div>
        <div className="mt-3 h-1.5 bg-brand-linen rounded-full overflow-hidden">
          <div className="h-full bg-brand-ink transition-all" style={{ width: `${((current + 1) / quiz.questions.length) * 100}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
            <h1 className="font-serif text-4xl text-brand-ink mt-10 leading-tight">{q.question}</h1>
            <div className="mt-8 space-y-3">
              {q.options.map((opt, i) => {
                const selected = answers[current] === i;
                return (
                  <button key={i} data-testid={T.quizOption} onClick={() => pick(i)}
                    className={`w-full text-left p-5 rounded-2xl border transition-all flex items-center gap-4 ${selected ? "bg-brand-ink text-brand-bone border-brand-ink" : "bg-white border-brand-linen hover:border-brand-ink hover:bg-brand-bone"}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${selected ? "bg-brand-bone text-brand-ink" : "bg-brand-bone text-brand-secondary border border-brand-linen"}`}>
                      {String.fromCharCode(65 + i)}
                    </div>
                    <div className="flex-1">{opt}</div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <button onClick={() => setCurrent(Math.max(0, current - 1))} disabled={current === 0}
            className="inline-flex items-center gap-2 text-brand-secondary hover:text-brand-ink px-4 py-2 disabled:opacity-40">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Previous
          </button>
          {isLast ? (
            <button data-testid={T.quizSubmit} onClick={submit} disabled={answers.includes(null) || busy}
              className="inline-flex items-center gap-2 bg-brand-accent text-white rounded-full px-6 py-3 hover:bg-brand-accentHover disabled:opacity-60">
              Submit assessment
            </button>
          ) : (
            <button data-testid={T.quizNext} onClick={() => setCurrent(current + 1)} disabled={!canProceed}
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover disabled:opacity-60">
              Next <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
